import React from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { UTC_INPUT_HINT, showLocalWhen, utcInputToIso } from '../../utils/utcDateTime';
import RichTextEditor from './RichTextEditor';
import ImageUploadField from '../shared-components/ImageUploadField';
import VideoUploadField from '../shared-components/VideoUploadField';
import './shopHourModal.css';

const ShopHourForm = ({
  formData,
  onChange,
  dateError,
  onSaveTemplate,
  onLoadTemplate,
  showActiveToggle = true,
  showShopHoursPassedToggle = false,
  token,
  onVideoBusyChange,
}) => {
  const handleInputChange = field => event => onChange(field, event.target.value);
  const handleCheckboxChange = field => event => onChange(field, event.target.checked);

  const sanitizedPreview = DOMPurify.sanitize(formData.description || '');
  const sanitizedShopDescription = DOMPurify.sanitize(formData.shopDescription || '');

  const parseUtcInput = value => {
    const iso = utcInputToIso(value);
    if (!iso) return null;
    const parsed = DateTime.fromISO(iso, { zone: 'utc' });
    return parsed.isValid ? parsed : null;
  };

  const startDate = parseUtcInput(formData.startTime);
  const endDate = parseUtcInput(formData.endTime);
  const hasSchedule = Boolean(startDate && endDate);
  const formatUtcFace = (dt, sameDayEnd = false) =>
    dt.setZone('utc').toFormat(sameDayEnd ? 'h:mm a' : "ccc, LLL d 'at' h:mm a");

  let scheduleLabel = 'Add start and end times to preview the schedule.';
  if (hasSchedule && startDate && endDate) {
    const sameDay = startDate.setZone('utc').hasSame(endDate.setZone('utc'), 'day');
    scheduleLabel = `${formatUtcFace(startDate)} - ${formatUtcFace(endDate, sameDay)} UTC`;
  }

  const localStart = showLocalWhen(utcInputToIso(formData.startTime));
  const localEnd = showLocalWhen(utcInputToIso(formData.endTime));
  const utcStartFace = formData.startTime ? String(formData.startTime).replace('T', ' ') : '';
  const utcEndFace = formData.endTime ? String(formData.endTime).replace('T', ' ') : '';
  const localReadback =
    hasSchedule &&
    localStart &&
    localEnd &&
    (localStart !== utcStartFace || localEnd !== utcEndFace)
      ? `Local: ${localStart} – ${localEnd}`
      : '';

  const now = DateTime.utc();
  const statusDetails = (() => {
    if (formData.shopHoursPassed) {
      return { label: 'Ended', variant: 'ended' };
    }
    if (formData.active) {
      return { label: 'Live now', variant: 'live' };
    }
    if (startDate && endDate) {
      if (endDate < now) return { label: 'Ended', variant: 'ended' };
      if (startDate > now) return { label: 'Upcoming', variant: 'upcoming' };
      return { label: 'Live now', variant: 'live' };
    }
    return { label: 'Draft', variant: 'draft' };
  })();

  const relativeStart = startDate ? startDate.toRelative({ base: now }) : null;
  const relativeLabel = (() => {
    if (!relativeStart) {
      return hasSchedule ? null : 'Set a start and end time to preview scheduling details.';
    }
    if (statusDetails.variant === 'upcoming') {
      return `Opens ${relativeStart}`;
    }
    if (statusDetails.variant === 'ended') {
      return `Ended ${relativeStart}`;
    }
    return `Started ${relativeStart}`;
  })();
  const previewTitle = formData.name?.trim() || 'Untitled shop hour';
  const logoFallbackInitial = previewTitle.charAt(0).toUpperCase();
  const hasBackground = Boolean(formData.backgroundMobileImageUrl);

  return (
    <Form className="shop-hour-form">
      <Row className="g-4">
        <Col lg={4}>
          <div className="section-card">
            <div className="section-header">
              <h6 className="section-title">Shop Details</h6>
              <p className="section-subtitle">Keep the basics tight and on-brand.</p>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Shop Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter shop name"
                value={formData.name}
                onChange={handleInputChange('name')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Shop Navigation Button</Form.Label>
              <ImageUploadField
                value={formData.shopButtonImageUrl}
                onChange={url => onChange('shopButtonImageUrl', url)}
                token={token}
                id="shop-hour-button-image"
              />
            </Form.Group>

            {showActiveToggle && (
              <Form.Group className="mb-2">
                <Form.Check
                  type="switch"
                  id="shop-hour-active-toggle"
                  label="Show this shop hour"
                  checked={Boolean(formData.active)}
                  onChange={handleCheckboxChange('active')}
                />
              </Form.Group>
            )}

            {showShopHoursPassedToggle && (
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="shop-hour-passed-toggle"
                  label="Mark as ended"
                  checked={Boolean(formData.shopHoursPassed)}
                  onChange={handleCheckboxChange('shopHoursPassed')}
                />
              </Form.Group>
            )}
          </div>
        </Col>

        <Col lg={8}>
          <Row className="g-4">
            <Col md={6}>
              <div className="section-card h-100">
                <div className="section-header">
                  <h6 className="section-title">Schedule</h6>
                  <p className="section-subtitle">{UTC_INPUT_HINT}</p>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={handleInputChange('startTime')}
                  />
                  <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={handleInputChange('endTime')}
                  />
                  <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
                  {localReadback && (
                    <Form.Text muted className="d-block">
                      {localReadback}
                    </Form.Text>
                  )}
                  {dateError ? (
                    <div className="text-danger small mt-1">{dateError}</div>
                  ) : (
                    <Form.Text muted>Ensure the window leaves room for promo lead time.</Form.Text>
                  )}
                </Form.Group>
              </div>
            </Col>

            <Col md={6}>
              <div className="section-card h-100">
                <div className="section-header">
                  <h6 className="section-title">Branding</h6>
                  <p className="section-subtitle">Optional imagery keeps the layout polished.</p>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Desktop Background Image</Form.Label>
                  <ImageUploadField
                    value={formData.backgroundImageUrl}
                    onChange={url => onChange('backgroundImageUrl', url)}
                    token={token}
                    id="shop-hour-bg-desktop"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Background Image</Form.Label>
                  <ImageUploadField
                    value={formData.backgroundMobileImageUrl}
                    onChange={url => onChange('backgroundMobileImageUrl', url)}
                    token={token}
                    id="shop-hour-bg-mobile"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Coin Shop Description Background</Form.Label>
                  <ImageUploadField
                    value={formData.shopDescriptionImageUrl}
                    onChange={url => onChange('shopDescriptionImageUrl', url)}
                    token={token}
                    id="shop-hour-description-image"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Event Description Video</Form.Label>
                  <VideoUploadField
                    value={formData.shopVideoUrl}
                    onChange={url => onChange('shopVideoUrl', url)}
                    onBusyChange={onVideoBusyChange}
                    token={token}
                    id="shop-hour-video"
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      <div className="section-card mt-4">
        <div className="section-header d-flex align-items-center justify-content-between">
          <div>
            <h6 className="section-title mb-0">Announcement Content</h6>
            <p className="section-subtitle mb-0">
              Craft a clear, friendly message for your players.
            </p>
          </div>
          {(onSaveTemplate || onLoadTemplate) && (
            <div className="template-actions">
              {onSaveTemplate && (
                <Button variant="outline-secondary" size="sm" onClick={onSaveTemplate}>
                  Save Template
                </Button>
              )}
              {onLoadTemplate && (
                <Button variant="outline-secondary" size="sm" onClick={onLoadTemplate}>
                  Load Template
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="editor-preview-grid rich-text-wrapper mt-3">
          <div className="rich-text-editor-pane">
            <RichTextEditor
              value={formData.description}
              onChange={value => onChange('description', value)}
              placeholder="Enter announcement content..."
            />
          </div>
          <div className="preview-pane">
            <div className="preview-header">
              <div>
                <div className="preview-title">Live Preview</div>
                <p className="preview-helper-text mb-0">
                  Matches the player-facing announcement layout.
                </p>
              </div>
              <span className={`announcement-status announcement-status--${statusDetails.variant}`}>
                {statusDetails.label}
              </span>
            </div>

            <div
              className={`announcement-preview ${
                hasBackground ? 'announcement-preview--image' : 'announcement-preview--fallback'
              }`}
            >
              <div
                className="announcement-canvas"
                style={
                  hasBackground
                    ? {
                        backgroundImage: `linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.85) 82%), url(${formData.backgroundMobileImageUrl})`,
                      }
                    : undefined
                }
              >
                <div className="announcement-headline-row">
                  <div className="announcement-logo-wrap">
                    {formData.shopLogoImageUrl ? (
                      <img src={formData.shopLogoImageUrl} alt={`${previewTitle} logo`} />
                    ) : (
                      <span>{logoFallbackInitial}</span>
                    )}
                  </div>
                  <div className="announcement-meta">
                    <span className="announcement-badge">Shop Hour</span>
                    <span className="announcement-window">{scheduleLabel}</span>
                    {relativeLabel && (
                      <span className="announcement-relative-meta">{relativeLabel}</span>
                    )}
                  </div>
                </div>
                <div className="announcement-body">
                  <h5 className="announcement-title">{previewTitle}</h5>
                  {sanitizedPreview ? (
                    <div
                      className="announcement-content"
                      dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
                    />
                  ) : (
                    <p className="announcement-placeholder mb-0">
                      Use the editor to craft your message. Headings, lists, and links render here
                      exactly as they do for players.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section-card mt-4">
        <div className="section-header">
          <h6 className="section-title mb-0">Coin Shop Description</h6>
          <p className="section-subtitle mb-0">Describe what players can find in the coin shop.</p>
        </div>

        <div className="editor-preview-grid rich-text-wrapper mt-3">
          <div className="rich-text-editor-pane">
            <RichTextEditor
              value={formData.shopDescription}
              onChange={value => onChange('shopDescription', value)}
              placeholder="Enter coin shop description..."
            />
          </div>
          <div className="preview-pane">
            <div className="preview-header">
              <div>
                <div className="preview-title">Live Preview</div>
                <p className="preview-helper-text mb-0">
                  Matches the player-facing coin shop layout.
                </p>
              </div>
            </div>

            <div
              style={{
                backgroundColor: '#9cb88b',
                borderRadius: '16px',
                padding: '24px',
                position: 'relative',
                overflow: 'hidden',
                minHeight: '360px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {formData.shopDescriptionImageUrl && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${formData.shopDescriptionImageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.25)',
                    }}
                  />
                </div>
              )}
              {sanitizedShopDescription ? (
                <div
                  className="coin-shop-description-preview"
                  style={{
                    fontFamily:
                      "'RecoletaAlt-Light', -apple-system, BlinkMacSystemFont, sans-serif",
                    fontSize: '20px',
                    color: '#ffffff',
                    lineHeight: 1.6,
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizedShopDescription }}
                />
              ) : (
                <p
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                    margin: 0,
                  }}
                >
                  Use the editor to craft your coin shop description. It will preview here matching
                  the player-facing layout.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default ShopHourForm;
